// src/components/products/ProductForm/TourDetails.jsx

import React, { useMemo, useState } from "react";
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageToggle from "../../global/LanguageToggle";
import useGetPackagesForSelect from "../../../hooks/packages/useGetPackagesForSelect";

// ✅ Import Custom Hooks & Helpers
import useFormLanguage from "../../../hooks/forms/useFormLanguage";
import { handleMultiLangChange as multiLangHelper } from "../../../utils/forms/multiLangHelper";
import { createAccordionHandler, isPanelExpanded } from "../../../utils/forms/accordionHelper";

// Import Section Components
import TourBasicInfoSection from "./TourDetailsSections/TourBasicInfoSection";
import DefaultTypeSection from "./TourDetailsSections/DefaultTypeSection";
import MorningEveningTypeSection from "./TourDetailsSections/MorningEveningTypeSection";
import TourHighlightsSection from "./TourDetailsSections/TourHighlightsSection";
import TourDescriptionsSection from "./TourDetailsSections/TourDescriptionsSection";
import TourGuidanceSection from "./TourDetailsSections/TourGuidanceSection";
import TourTransferAreasSection from "./TourDetailsSections/TourTransferAreasSection";

const TourDetails = ({ formData, setFormData, errors = {} }) => {
    // ✅ استخدام Custom Hook للغة
    const { isArabic, activeLang, setActiveLang } = useFormLanguage();

    // ✅ Accordion state - يدعم فتح عدة أكورديونات
    const [expandedPanels, setExpandedPanels] = useState(['panel1']);
    const handleAccordionChange = createAccordionHandler(setExpandedPanels, true);

    // Get packages
    const { options: packages, isLoading: loadingPackages } = useGetPackagesForSelect();
    const packagesOptions = useMemo(() => {
        if (!packages) return [];
        return packages?.map(pkg => ({
            id: pkg.value,
            name: pkg.label,
            price: pkg.price
        }));
    }, [packages]);

    // Tour type (default or morning_and_evening)
    const tourType = formData.type || 'default';
    const isDefaultType = tourType === 'default';
    const isMorningEveningType = tourType === 'morning_and_evening';

    // ✅ استخدام Multi-lang Helper
    const handleFieldChange = (field, lang, value) => {
        multiLangHelper(setFormData, formData, field, lang, value);
    };

    // Handle tour type change
    const handleTourTypeChange = (e) => {
        const newType = e.target.value;
        setFormData({
            ...formData,
            type: newType,
        });
    };

    // Shared props for all sections
    const commonProps = {
        formData,
        setFormData,
        errors,
        isArabic,
        activeLang,
        handleMultiLangChange: handleFieldChange,
    };

    return (
        <Paper sx={{ p: 4 }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {isArabic ? 'تفاصيل الجولة (Tour)' : 'Tour Details'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? 'أدخل تفاصيل الجولة السياحية'
                            : 'Enter the tour details'}
                    </Typography>
                </Box>

                <LanguageToggle
                    activeLang={activeLang}
                    onChange={(lang) => setActiveLang(lang)}
                    showLabel={true}
                />
            </Box>

            {/* Accordions */}
            <Box>
                {/* 1. Basic Info (Package & Type) */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel1')}
                    onChange={handleAccordionChange('panel1')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📦 المعلومات الأساسية' : '📦 Basic Information'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TourBasicInfoSection
                            {...commonProps}
                            packagesOptions={packagesOptions}
                            loadingPackages={loadingPackages}
                            tourType={tourType}
                            handleTourTypeChange={handleTourTypeChange}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 2. Operating Hours & Itinerary (Conditional based on type) */}
                {isDefaultType && (
                    <Accordion
                        expanded={isPanelExpanded(expandedPanels, 'panel2')}
                        onChange={handleAccordionChange('panel2')}
                        sx={{ mb: 2 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 600 }}>
                                {isArabic ? '⏰ أوقات التشغيل وخط السير' : '⏰ Operating Hours & Itinerary'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <DefaultTypeSection {...commonProps} />
                        </AccordionDetails>
                    </Accordion>
                )}

                {isMorningEveningType && (
                    <Accordion
                        expanded={isPanelExpanded(expandedPanels, 'panel2')}
                        onChange={handleAccordionChange('panel2')}
                        sx={{ mb: 2 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 600 }}>
                                {isArabic ? '🌅 الفترات الصباحية والمسائية' : '🌅 Morning & Evening Sessions'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <MorningEveningTypeSection {...commonProps} />
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* 3. Highlights */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel3')}
                    onChange={handleAccordionChange('panel3')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '⭐ أبرز المعالم' : '⭐ Highlights'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TourHighlightsSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 4. Long Description */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel4')}
                    onChange={handleAccordionChange('panel4')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📖 الوصف التفصيلي' : '📖 Detailed Description'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TourDescriptionsSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 5. What to Bring & Voucher Notes */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel5')}
                    onChange={handleAccordionChange('panel5')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '🎒 إرشادات وملاحظات' : '🎒 Guidelines & Notes'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TourGuidanceSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 6. Transfer Areas */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel6')}
                    onChange={handleAccordionChange('panel6')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '🚌 مناطق النقل' : '🚌 Transfer Areas'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TourTransferAreasSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Paper>
    );
};

export default TourDetails;
